<script setup>
import {computed, useSlots} from "vue";
import IconLocation from "@/assets/icons/IconLocation.vue";
import IconCross from "@/assets/icons/IconCross.vue";

const props = defineProps({
  label: {
    type: String,
    required: false,
    default: "",
  },
  color: {
    type: String,
    default: "",
    validator: (value) => ["gray", "red", "orange", "yellow", "green", ""].includes(value)
  },
  type: {
    type: String,
    default: "switcher",
    validator: (value) => ["switcher", "text"].includes(value)
  },
  modelValue: {
    type: Boolean,
    default: false
  },
  useCrossIcon: {
    type: Boolean,
    default: true
  },
  useLocationIcon: {
    type: Boolean,
    default: false
  },
  width: {
    type: [String, Number],
    default: null,
  },
  height: {
    type: [String, Number],
    default: null,
  },
  maxWidth: {
    type: [String, Number],
    default: null,
  },
  maxHeight: {
    type: [String, Number],
    default: null,
  },
  useHover: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(["update:modelValue", "cancel"]);
const slots = useSlots();

// Функция для нормализации размеров
const normalizeSize = (size) => {
  if (!size) return null;
  if (typeof size === "number") {
    return `${size}px`;
  }
  return size;
};

// Вычисляемое свойство стилей
const chipStyles = computed(() => {
  const styles = {};

  if (props.width) {
    styles.width = normalizeSize(props.width);
  }

  if (props.height) {
    styles.height = normalizeSize(props.height);
  }

  if (props.maxWidth) {
    styles.maxWidth = normalizeSize(props.maxWidth);
  }

  if (props.maxHeight) {
    styles.maxHeight = normalizeSize(props.maxHeight);
  }

  return styles;
});

// Вычисляемые свойства классов
const chipClasses = computed(() => {
  const classes = ["chip"];

  // Добавляем цветовой класс
  if (props.type === "text" || (props.type === "switcher" && props.modelValue)) {
    classes.push(`chip--${props.color}`);
  }

  // Добавляем класс для ограничения ширины (для overflow-ellipsis)
  if (props.width || props.maxWidth) {
    classes.push("chip--constrained-width");
  }

  // Добавляем класс когда hover отключен
  if (!props.useHover) {
    classes.push("chip--no-hover");
  }

  return classes;
});

// Методы
const handleChipClick = () => {
  if (props.type === "switcher") {
    emit("update:modelValue", !props.modelValue);
  }
};

const handleCancelClick = (event) => {
  event.stopPropagation(); // Предотвращаем всплытие события
  emit("cancel");
};
</script>

<template>
  <div
    :class="chipClasses"
    :style="chipStyles"
    @click="handleChipClick"
  >
    <IconLocation
      v-if="useLocationIcon"
      class="chip__icon chip__icon--location"
    />

    <span
      v-if="(label && label.length > 0) || slots['default']"
      class="chip__label"
    >{{ label }}<slot /></span>

    <IconCross
      v-if="useCrossIcon"
      class="chip__icon chip__icon--cross"
      @click="handleCancelClick"
    />
  </div>
</template>

<style scoped lang="scss">
.chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 20px;
  padding: 4px 12px;

  background: var(--background-01);
  color: var(--text-text-pur);

  &__label {
    user-select: none;
    overflow-wrap: anywhere;
    flex: 1;
    min-width: 0; // Важно для работы text-overflow: ellipsis
  }

  &__icon {
    flex-shrink: 0;

    &--cross {
      cursor: pointer;
      user-select: none;
    }
  }

  // Модификатор для ограниченной ширины
  &--constrained-width {
    .chip__label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow-wrap: normal; // Переопределяем overflow-wrap для ellipsis
    }
  }

  &:not(&--no-hover) {
    &:hover {
      color: var(--text-text-pur);
      background: var(--background-bg-hover);
    }
  }

  // Цветовые модификаторы
  &--gray {
    background: var(--purple-purple80);
    color: var(--text-text-on-inverse);

    &:not(.chip--no-hover) {
      &:hover {
        background: var(--purple-purple50);
        color: var(--text-text-on-inverse)
      }
    }
  }

  &--red {
    background: var(--point-emoji-red);
    color: var(--text-text-on-inverse);

    &:not(.chip--no-hover) {
      &:hover {
        color: var(--text-text-on-inverse);
        background: var(--red-red70);
      }
    }
  }

  &--orange {
    background: var(--point-emoji-orange);
    color: var(--text-text-on-inverse);

    &:not(.chip--no-hover) {
      &:hover {
        color: var(--text-text-on-inverse);
        background: var(--orange-orange70);
      }
    }
  }

  &--yellow {
    background: var(--point-emoji-yellow);
    color: var(--text-text-main);

    &:not(.chip--no-hover) {
      &:hover {
        color: var(--text-text-main);
        background: var(--yellow-yellow60);
      }
    }
  }

  &--green {
    background: var(--point-emoji-green);
    color: var(--text-text-main);

    &:not(.chip--no-hover) {
      &:hover {
        color: var(--text-text-main);
        background: var(--green-green10);
      }
    }
  }
}
</style>